import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Switch, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { SITE_CONTENT } from "./constants/siteContent";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import FloatingSocialBar from "./components/layout/FloatingSocialBar";
import ContactDock from "./components/layout/ContactDock";
import ScrollToTopButton from "./components/layout/ScrollToTopButton";
import Preloader from "./components/anim/Preloader";
import HomePage from "./components/pages/HomePage";
import ContentPage from "./components/pages/ContentPage";
import ContactPage from "./components/pages/ContactPage";
import ZetaPage from "./components/pages/ZetaPage";
import { setAnalyticsUserContext, trackPageView } from "./utils/analytics";
import {
  resetPageTracking,
  startAutoTracking
} from "./utils/analyticsAutoTrack";
import { getSeoForPath, getStructuredData } from "./utils/seo";

function setOrUpdateMeta(nameOrProperty, content, isProperty = false) {
  const selector = isProperty
    ? `meta[property="${nameOrProperty}"]`
    : `meta[name="${nameOrProperty}"]`;
  let tag = document.head.querySelector(selector);

  if (!tag) {
    tag = document.createElement("meta");
    if (isProperty) {
      tag.setAttribute("property", nameOrProperty);
    } else {
      tag.setAttribute("name", nameOrProperty);
    }
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
}

function setOrUpdateLink(rel, href) {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);

  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }

  tag.setAttribute("href", href);
}

function setOrUpdateJsonLd(id, data) {
  let script = document.head.querySelector(`#${id}`);

  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  const script = document.head.querySelector(`#${id}`);
  if (script) {
    script.remove();
  }
}

function syncJsonLd(id, data) {
  if (data) {
    setOrUpdateJsonLd(id, data);
    return;
  }

  removeJsonLd(id);
}

function RouteSeoManager() {
  const location = useLocation();

  useEffect(() => {
    const seo = getSeoForPath(location.pathname);
    const robotsContent = seo.isKnown ? "index, follow" : "noindex, follow";

    document.title = seo.title;
    setOrUpdateMeta("description", seo.description);
    setOrUpdateMeta("keywords", seo.keywords);
    setOrUpdateMeta("robots", robotsContent);
    setOrUpdateMeta("og:type", "website", true);
    setOrUpdateMeta("og:site_name", "Appinfigo", true);
    setOrUpdateMeta("og:title", seo.title, true);
    setOrUpdateMeta("og:description", seo.description, true);
    setOrUpdateMeta("og:url", seo.canonicalUrl, true);
    setOrUpdateMeta("og:image", seo.image, true);
    setOrUpdateMeta("twitter:card", "summary_large_image");
    setOrUpdateMeta("twitter:title", seo.title);
    setOrUpdateMeta("twitter:description", seo.description);
    setOrUpdateMeta("twitter:image", seo.image);
    setOrUpdateLink("canonical", seo.canonicalUrl);

    const structuredData = getStructuredData({ pathname: location.pathname, seo });
    syncJsonLd("schema-organization", structuredData.organization);
    syncJsonLd("schema-website", structuredData.website);
    syncJsonLd("schema-webpage", structuredData.webPage);
    syncJsonLd("schema-breadcrumb", structuredData.breadcrumbList);
    syncJsonLd("schema-service", structuredData.service);
    syncJsonLd("schema-faq", structuredData.faq);
    syncJsonLd("schema-contact-page", structuredData.contactPage);
    syncJsonLd("schema-pricing", structuredData.priceList);
  }, [location.pathname]);

  return null;
}

function RouteAnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    /* Flush what the previous route earned and zero the depth/engagement
       counters. Without this an SPA reports one session-long "page" and
       every scroll-depth and attention number is meaningless. */
    resetPageTracking(location.pathname);
    trackPageView(location.pathname, document.title);
  }, [location.pathname]);

  return null;
}

function RouteScrollReset() {
  const location = useLocation();

  useEffect(() => {
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [location.pathname]);

  return null;
}

/* Signature left-to-right clip-wipe on every section heading as it scrolls into
   view. Applied globally so no per-section wiring is needed. Reduced-motion safe.

   Why this does NOT use an IntersectionObserver on the heading: an observer
   honours the TARGET's own clip-path. A heading clipped to inset(0 100% 0 0)
   has an empty visible rect, so with any threshold above 0 it never reports as
   intersecting (measured in Chromium 148: isIntersecting false, ratio 0). The
   wipe therefore never fired on scroll — every heading was just un-clipped by a
   2.6s fail-safe, usually off-screen where nobody saw it. getBoundingClientRect()
   ignores clip-path, so the trigger reads that instead.

   A heading can never be left clipped: the same check runs on scroll, on resize
   and on a slow heartbeat (layout can move a heading into view with no scroll
   event — images loading, an accordion closing above it), and anything already
   scrolled past, or not rendered at all (display: none), is released at once. */
function HeadingWipe() {
  const location = useLocation();

  useEffect(() => {
    const prefersReduced =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      return undefined;
    }

    const pending = new Set();
    let frame = 0;

    const reveal = (heading) => {
      pending.delete(heading);
      heading.dataset.wipe = "done";
      heading.style.clipPath = "inset(0 0 0 0)";
      heading.style.webkitClipPath = "inset(0 0 0 0)";
    };

    const adopt = () => {
      document.querySelectorAll("main h2").forEach((heading) => {
        if (heading.dataset.wipe) {
          return;
        }
        heading.dataset.wipe = "pending";
        // neutralize any per-section Reveal fade-up so the heading animates with
        // the clean clip-wipe ONLY, site-wide
        heading.style.opacity = "1";
        heading.style.transform = "none";
        heading.style.clipPath = "inset(0 100% 0 0)";
        heading.style.webkitClipPath = "inset(0 100% 0 0)";
        heading.style.transition = "clip-path 0.65s cubic-bezier(0.16, 1, 0.3, 1)";
        pending.add(heading);
      });
    };

    const check = () => {
      frame = 0;
      const trigger = window.innerHeight * 0.95;
      pending.forEach((heading) => {
        if (!heading.isConnected) {
          pending.delete(heading);
          return;
        }
        const rect = heading.getBoundingClientRect();
        // in view, already scrolled past, or not rendered (all-zero rect)
        if (rect.top < trigger) {
          reveal(heading);
        }
      });
    };

    const schedule = () => {
      if (!frame) {
        frame = window.requestAnimationFrame(check);
      }
    };

    // Let the route's sections mount and lay out first; the clip is applied and
    // checked in the same task, so a heading in view wipes on the next frame.
    const timer = window.setTimeout(() => {
      adopt();
      schedule();
    }, 90);
    // Heartbeat: adopts headings that mount late and catches layout shifts.
    const heartbeat = window.setInterval(() => {
      if (document.hidden) {
        return;
      }
      adopt();
      if (pending.size) {
        schedule();
      }
    }, 1200);

    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });

    return () => {
      window.clearTimeout(timer);
      window.clearInterval(heartbeat);
      window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      // never strand a heading clipped across a route change
      pending.forEach(reveal);
    };
  }, [location.pathname]);

  return null;
}

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  /* One set of delegated listeners for the whole site: contact-channel and
     outbound clicks, scroll depth, section views and engaged time. Started
     once here rather than per component, so any component rendering a
     tel:/mailto:/wa.me link is tracked without opting in. */
  useEffect(() => startAutoTracking(), []);

  useEffect(() => {
    setAnalyticsUserContext({
      visitor_type: "business_user",
      site_section: "marketing"
    });
  }, []);

  // Smooth inertia scrolling (the signature feel of the reference). Disabled
  // under prefers-reduced-motion; exposed on window for programmatic scrolling
  // (route reset, capture tooling).
  useEffect(() => {
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      return undefined;
    }

    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true
    });
    window.__lenis = lenis;

    let rafId = 0;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      if (window.__lenis === lenis) {
        delete window.__lenis;
      }
    };
  }, []);

  const testimonialsByColumn = [
    SITE_CONTENT.testimonials.items.slice(0, 3),
    SITE_CONTENT.testimonials.items.slice(3, 6),
    SITE_CONTENT.testimonials.items.slice(6, 9)
  ];

  const handleNavItemClick = () => {
    setIsMenuOpen(false);
  };

  const handleFaqToggle = (index) => {
    setOpenFaqIndex((prev) => (prev === index ? -1 : index));
  };

  const notFoundPage = {
    eyebrow: "Page",
    title: "This page is not available",
    subtitle: "The page you requested may have moved. Use the links below to continue browsing.",
    sections: [
      {
        heading: "Go to Services",
        body: "Explore our software, cloud, AI, and security capabilities."
      },
      {
        heading: "Review Case Studies",
        body: "See delivery outcomes across industries and product categories."
      },
      {
        heading: "Contact Appinfigo",
        body: "Talk to our team for roadmap planning and execution support."
      }
    ]
  };

  return (
    <Router>
      <div className="page-shell">
        {/* Once-per-session lime curtain. Renders in a portal on <body>, plays
            nothing under prefers-reduced-motion, and takes over from the
            pre-React curtain in public/index.html. */}
        <Preloader />
        <RouteAnalyticsTracker />
        <RouteSeoManager />
        <RouteScrollReset />
        <HeadingWipe />
        {/* PromoBar hidden for the Appinventiv-style full-bleed hero (reference has no promo strip).
            To re-enable: uncomment <PromoBar /> below AND restore
            import PromoBar from "./components/layout/PromoBar"; at the top —
            an unused import fails the build under CI=true.
            Restore by uncommenting if the live-work marquee is wanted back. */}
        {/* <PromoBar /> */}
        <Header
          isMenuOpen={isMenuOpen}
          onToggleMenu={() => setIsMenuOpen((prev) => !prev)}
          onNavItemClick={handleNavItemClick}
        />

        <Switch>
          <Route exact path="/">
            <HomePage
              openFaqIndex={openFaqIndex}
              handleFaqToggle={handleFaqToggle}
              testimonialsByColumn={testimonialsByColumn}
            />
          </Route>
          <Route exact path="/contact">
            <ContactPage />
          </Route>
          {/* /zeta is the app's download page. Note the three /zeta/*
              legal documents are STATIC html served before the SPA (see
              netlify.toml), so they never reach React at all. */}
          <Route exact path="/zeta">
            <ZetaPage />
          </Route>

          {SITE_CONTENT.pages.filter((page) => page.path !== "/contact").map((page) => (
            <Route key={page.path} exact path={page.path}>
              <ContentPage page={page} />
            </Route>
          ))}
          <Route>
            <ContentPage page={notFoundPage} />
          </Route>
        </Switch>

        <Footer onNavItemClick={handleNavItemClick} />
        {/* ContactDock owns the direct WhatsApp/Call path. It MUST stay mounted:
            Header hides its WhatsApp below 981px and FloatingSocialBar hides
            itself below 768px, both assuming the dock covers mobile. */}
        <ContactDock />
        <FloatingSocialBar />
        <ScrollToTopButton />
      </div>
    </Router>
  );
}

export default App;
